import { styled } from "goober";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { playClick } from "../lib/sounds";
import { writeToClipboard } from "../lib/writeToClipboard";
import { AnimatedCopyIcon } from "./AnimatedCopyIcon";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { ToggleButton } from "./HeadingToggle";
import { Icon, type IconName } from "./Icon";
import { ShortcutHint } from "./ShortcutHint";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { UrlDisplay } from "./UrlDisplay";

export type BrowserLinkItem = {
  key: string;
  icon: IconName;
  label: string;
  metadata: string;
  selected: boolean;
  onClick: () => void;
};

type Props = {
  displayUrl: string;
  openUrl?: string;
  links?: ReadonlyArray<BrowserLinkItem>;
  onReload?: () => void;
  onBack?: () => void;
  onForward?: () => void;
  canBack?: boolean;
  canForward?: boolean;
  inspectMode?: boolean;
  onInspectModeChange?: (mode: boolean) => void;
  children: ReactNode;
};

const StyledBrowserChrome = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const Container = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 8px;
  flex-shrink: 0;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const UrlBar = styled("div")`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 9999px;
  background: var(--alpha4);
  min-width: 0;
  box-sizing: border-box;

  &:hover {
    background: var(--alpha8);
  }

  &:hover [data-url-action] {
    opacity: 1;
  }
`;

const UrlSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const UrlText = styled("div")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  text-align: center;
  font-family: inherit;
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
`;

const UrlAction = styled("div")`
  display: inline-flex;
  flex-shrink: 0;
  margin-right: -10px;
  opacity: 0;
  transition: opacity 0.15s ease-out;

  &[data-visible] {
    opacity: 1;
  }
`;

const KebabTrigger = styled("button")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 0 9999px 9999px 0;
  cursor: pointer;
  transition:
    background 0.15s ease-out,
    color 0.15s ease-out;

  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const FrameWrap = styled("div")`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  justify-content: center;
`;

// While a dropdown is open, the preview iframe would otherwise swallow clicks (an
// iframe never dispatches mousedown to the parent document), so clicking the page
// couldn't dismiss the menu. This transparent cover keeps that click in the parent
// document, where the dropdown's outside-click detection sees it.
const IframeCover = styled("div")`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const MOBILE_VIEWPORT_WIDTH = 390;

const Viewport = styled("div")`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  transition:
    width 240ms cubic-bezier(0.4, 0, 0.2, 1),
    height 240ms cubic-bezier(0.4, 0, 0.2, 1),
    margin 240ms cubic-bezier(0.4, 0, 0.2, 1);

  &[data-viewport="mobile"] {
    width: ${MOBILE_VIEWPORT_WIDTH}px;
    max-width: 100%;
    height: calc(100% - 24px);
    margin: 12px 0;
    border: 0.5px solid var(--border-alpha);
    border-radius: 12px;
    overflow: hidden;
    background: var(--surface-primary);
    box-shadow: var(--elevation-low);
  }
`;

const InspectButton = styled(ToggleButton)`
  width: auto;
  gap: 6px;
  padding: 0 8px;

  &[aria-pressed="true"] {
    background: var(--alpha4);
  }
`;

const ViewportButton = styled(ToggleButton)`
  &[aria-pressed="true"] {
    background: var(--alpha4);
  }
`;

function UrlActionsMenu({
  openUrl,
  menuOpen,
  onOpenChange,
}: {
  openUrl: string;
  menuOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);
  const copiedResetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    return () => {
      if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    };
  }, []);

  const copyLink = () => {
    void writeToClipboard(openUrl);
    setCopied(true);
    if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    copiedResetTimeout.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <UrlAction
      data-url-action=""
      data-visible={menuOpen || copied ? "" : undefined}
    >
      <Dropdown
        align="bottom"
        minWidth={162}
        maxWidth={162}
        onOpenChange={onOpenChange}
        opener={
          <KebabTrigger type="button" aria-label="Link actions" data-no-pill="">
            <AnimatedCopyIcon copied={copied} icon="more" size={12} />
          </KebabTrigger>
        }
        sections={[
          {
            items: [
              {
                type: "button",
                key: "copy",
                left: <Icon name="copy" size={16} />,
                label: "Copy link",
                onClick: copyLink,
              },
              {
                type: "button",
                key: "open",
                left: <Icon name="arrow-up-right" size={16} />,
                label: "Open in new tab",
                onClick: () =>
                  window.open(openUrl, "_blank", "noopener,noreferrer"),
              },
            ],
          },
        ]}
      />
    </UrlAction>
  );
}

export function BrowserChrome({
  displayUrl,
  openUrl,
  links,
  onReload,
  onBack,
  onForward,
  canBack,
  canForward,
  inspectMode,
  onInspectModeChange,
  children,
}: Props) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");

  const urlBar = (
    <UrlBar
      onMouseDown={(e) => {
        if (e.button !== 0 || !e.metaKey || !openUrl) return;
        e.stopPropagation();
        playClick();
        window.open(openUrl, "_blank", "noopener,noreferrer");
      }}
    >
      <UrlText>
        <UrlDisplay url={displayUrl} />
      </UrlText>
      {openUrl && (
        <UrlActionsMenu
          openUrl={openUrl}
          menuOpen={menuOpen}
          onOpenChange={setMenuOpen}
        />
      )}
    </UrlBar>
  );

  return (
    <StyledBrowserChrome>
      <Container>
        <Button
          variant="secondary"
          icon="arrow-left"
          type="button"
          onClick={onBack}
          disabled={!onBack || !canBack}
          aria-label="Back"
        />
        <Button
          variant="secondary"
          icon="arrow-right"
          type="button"
          onClick={onForward}
          disabled={!onForward || !canForward}
          aria-label="Forward"
        />
        <Button
          variant="secondary"
          icon="rotate-cw"
          type="button"
          onClick={onReload}
          disabled={!onReload}
          aria-label="Reload"
        />
        <UrlSlot>
          {links && links.length > 0 ? (
            <Dropdown
              align="bottom-center"
              minWidth={320}
              margin="6px 0 0"
              onOpenChange={setDropdownOpen}
              opener={urlBar}
              sections={[
                {
                  items: links.map((link) => ({
                    type: "button" as const,
                    key: link.key,
                    selected: link.selected,
                    left: <Icon name={link.icon} size={16} />,
                    label: link.label,
                    subtitle: (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {link.metadata}
                      </Text>
                    ),
                    right: link.selected ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : null,
                    onClick: link.onClick,
                  })),
                },
              ]}
            />
          ) : (
            urlBar
          )}
        </UrlSlot>
        <Tooltip content="Mobile view">
          <ViewportButton
            aria-pressed={viewport === "mobile"}
            aria-label="Toggle mobile view"
            disabled={!onInspectModeChange}
            onPress={() =>
              setViewport((v) => (v === "mobile" ? "desktop" : "mobile"))
            }
          >
            <Icon name="smartphone" size={12} />
          </ViewportButton>
        </Tooltip>
        <Tooltip
          content={
            <>
              Point and prompt <ShortcutHint>⇧⌥I</ShortcutHint>
            </>
          }
        >
          <InspectButton
            aria-pressed={!!inspectMode}
            aria-label="Toggle inspect"
            disabled={!onInspectModeChange}
            onPress={() => onInspectModeChange?.(!inspectMode)}
          >
            <Icon name="mouse-pointer-2" size={12} />
            <Text variant="text-small-md">Inspect</Text>
          </InspectButton>
        </Tooltip>
      </Container>
      <FrameWrap>
        <Viewport data-viewport={viewport}>{children}</Viewport>
        {(dropdownOpen || menuOpen) && <IframeCover />}
      </FrameWrap>
    </StyledBrowserChrome>
  );
}
